// One product: why it is on the list, margin, reach by account type, momentum and
// which customers to offer it to. Uses the row the Products list already loaded.
import { useRef } from "react";
import DetailPanel, { PanelHead } from "./DetailPanel";
import { plural } from "./customerText";
import { count, money, pct, signedPct } from "./format";

const TAG_LABELS = {
  opportunity: "Opportunity", rising: "Rising", falling: "Falling", not_reordered: "Not reordered", low_margin: "Low margin",
};

export function productNotes(row) {
  const notes = [];
  const tags = new Set(row.tags || []);
  if (tags.has("opportunity")) {
    notes.push(`Up to ${money(row.potential_weekly)} a week more if similar customers bought it the way current buyers do`
      + (row.potential_profit_weekly != null ? ` (${money(row.potential_profit_weekly)} a week profit).` : "."));
  }
  if (row.customers_gained !== undefined && (tags.has("rising") || tags.has("falling"))) {
    notes.push(`${count(row.customers_gained)} new ${plural(row.customers_gained, "buyer")} and ${count(row.customers_lost)} lost since the comparison dates`
      + (row.sales_change_percent != null ? `; sales ${signedPct(row.sales_change_percent)}.` : "."));
  }
  if (tags.has("not_reordered")) {
    notes.push(`Only ${pct(row.reorder_rate_percent, 0)} of buyers bought it again on another day. Worth asking why before pushing it.`);
  }
  if (tags.has("low_margin")) notes.push(`Margin is ${pct(row.margin_percent)}, so selling more adds little profit.`);
  return notes;
}

export default function ProductPanel({ mode, row, onClose, onOpenCustomer, onFocus }) {
  const headingRef = useRef(null);
  if (!row) {
    return <DetailPanel mode={mode} label="Product details" onClose={onClose}>
      <div className="sa-panel-body">
        <PanelHead title="Product details" onClose={onClose} closeLabel="Close product details" headingRef={headingRef} />
        <p className="sa-panel-muted">This product isn’t in the list for these dates and filters.</p>
      </div>
    </DetailPanel>;
  }
  const notes = productNotes(row);
  const hasMomentum = row.customers_gained !== undefined;
  return <DetailPanel mode={mode} label="Product details" onClose={onClose}>
    <div className="sa-panel-body">
      <PanelHead title={row.name} subtitle={[row.sku, row.category, row.vendor].filter(Boolean).join(" · ")}
        onClose={onClose} closeLabel="Close product details" headingRef={headingRef} />

      {(row.tags?.length > 0 || notes.length > 0) && <div className="sa-panel-status">
        {row.tags?.length > 0 && <div className="sa-panel-tags">{row.tags.map((tag) => <span key={tag} className={`sa-badge sa-badge--${tag}`}>{TAG_LABELS[tag] || tag}</span>)}</div>}
        {notes.map((note) => <p key={note}>{note}</p>)}
      </div>}

      <dl className="sa-panel-stats">
        <div><dt>Sales per week</dt><dd>{money(row.weekly_sales)}<small>{money(row.sales)} in these dates</small></dd></div>
        <div><dt>Margin</dt><dd>{pct(row.margin_percent)}<small>{row.gross_profit != null ? `${money(row.gross_profit)} gross profit` : "no cost recorded"}</small></dd></div>
        <div><dt>Customers</dt><dd>{count(row.customers)}<small>{pct(row.reach_percent, 0)} of active customers</small></dd></div>
        <div><dt>Bought again</dt><dd>{row.customers >= 3 ? pct(row.reorder_rate_percent, 0) : "—"}<small>{count(row.repeat_customers)} on two or more days</small></dd></div>
        {hasMomentum && <div><dt>Buyers since comparison</dt><dd>+{count(row.customers_gained)} / −{count(row.customers_lost)}
          <small>sales {signedPct(row.sales_change_percent) ?? "new"}</small></dd></div>}
      </dl>

      {row.reach_by_account_type?.length > 0 && <section className="sa-panel-section" aria-label="Reach by account type">
        <h3>Who buys it, by account type</h3>
        <ul className="sa-panel-reach">{row.reach_by_account_type.map((item) => <li key={String(item.account_type_id)} className={item.counted ? "" : "is-muted"}>
          <span>{item.account_type}</span><span>{count(item.buyers)} of {count(item.customers)} ({pct(item.reach_percent, 0)})</span>
          <i aria-hidden="true"><b style={{ width: `${Math.min(100, Number(item.reach_percent || 0))}%` }} /></i>
          <small>{item.counted ? `${count(item.non_buyers)} not buying · up to ${money(item.potential_weekly)}/wk` : "Too few buyers to suggest others"}</small>
        </li>)}</ul>
      </section>}

      <section className="sa-panel-section" aria-label="Who to offer it to">
        <h3>Who to offer it to</h3>
        {row.targets?.length ? <ul className="sa-panel-list">{row.targets.map((target) => <li key={target.key}>
          <span><button type="button" className="sa-row-link" onClick={() => onOpenCustomer?.(target.key)}>{target.name}</button> · {target.account_type}</span>
          <span>spends {money(target.weekly_spend)}/wk</span>
        </li>)}</ul> : <p>{Number(row.potential_weekly) > 0
          ? "Similar customers aren’t named for products this far down the list."
          : "No account type has enough buyers yet to suggest similar customers."}</p>}
        {row.target_total > (row.targets?.length || 0) && row.targets?.length > 0 && <p className="sa-panel-muted">+{count(row.target_total - row.targets.length)} more similar customers not buying it.</p>}
      </section>

      <div className="sa-panel-actions">
        <button type="button" onClick={() => onFocus?.(row)}>Show the whole page for this product</button>
      </div>
    </div>
  </DetailPanel>;
}
